import { useEffect, useMemo, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { DateTime } from 'luxon'
import {
  faChevronDown,
  faChevronUp,
  faMagnifyingGlass,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons'
import { Badge, Button, Icon, Input, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import {
  CONVERSATION_VIEWS,
  RAIL_FILTERS,
  type ConversationView,
  type RailFilter,
} from '@/features/adviser-chat/lib/constants'
import {
  HAVEN_VIEW_QUERY_PARAM,
  isHavenPath,
} from '@/features/adviser-chat/lib/havenRoutes'
import { toRailRow } from '@/features/adviser-chat/lib/railRow'
import {
  useConversationRail,
  type RailSection,
} from '@/features/adviser-chat/hooks/useConversationRail'
import { ConversationRailRow } from './ConversationRailRow'

interface ConversationRailProps {
  activeConversationId: number | null
  onSelect: (id: number) => void
  onNewConversation?: () => void
}

const FILTER_LABELS: Record<RailFilter, string> = {
  [RAIL_FILTERS.all]: 'All',
  [RAIL_FILTERS.needsAttention]: 'Needs attention',
}

const matchesQuery = (title: string | null, needle: string): boolean =>
  !needle || (title ?? '').toLowerCase().includes(needle)

export const ConversationRail = ({
  activeConversationId,
  onSelect,
  onNewConversation,
}: ConversationRailProps) => {
  const { filter, setFilter, needsAttentionCount, sections, loadMore } =
    useConversationRail()
  const [query, setQuery] = useState('')
  const [collapsed, setCollapsed] = useState<Set<ConversationView>>(new Set())
  const needle = query.trim().toLowerCase()
  const [searchParams, setSearchParams] = useSearchParams()
  const { pathname, state } = useLocation()
  const [openTopPending, setOpenTopPending] = useState(false)

  // Only on /haven: a panel rail would take the link before the launch hook carries it there.
  // Consumed once, or picking All would snap back to it.
  useEffect(() => {
    const linkedFilter = Object.values(RAIL_FILTERS).find(
      (railFilter) => railFilter === searchParams.get(HAVEN_VIEW_QUERY_PARAM)
    )
    if (!isHavenPath(pathname) || !linkedFilter) return
    setFilter(linkedFilter)
    setOpenTopPending(linkedFilter === RAIL_FILTERS.needsAttention)
    const next = new URLSearchParams(searchParams)
    next.delete(HAVEN_VIEW_QUERY_PARAM)
    setSearchParams(next, { replace: true, state })
  }, [pathname, searchParams, setSearchParams, setFilter, state])

  const needsAttention = sections.find(
    (section) => section.view === CONVERSATION_VIEWS.needsAttention
  )
  useEffect(() => {
    const settled =
      needsAttention?.status === 'loaded' || needsAttention?.status === 'error'
    if (!openTopPending || !needsAttention || !settled) return
    setOpenTopPending(false)
    const top = needsAttention.conversations[0]
    if (top) onSelect(top.id)
  }, [openTopPending, needsAttention, onSelect])

  const now = useMemo(() => DateTime.now(), [])
  const visible = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        rows: section.conversations
          .filter((conversation) => matchesQuery(conversation.title, needle))
          .map((conversation) => toRailRow(conversation, now)),
      })),
    [needle, now, sections]
  )

  const toggleSection = (view: ConversationView) =>
    setCollapsed((previous) => {
      const next = new Set(previous)
      if (!next.delete(view)) next.add(view)
      return next
    })

  const loading = visible.some(
    (section) => section.status === 'loading' && section.rows.length === 0
  )
  const failedEmpty = visible.some(
    (section) =>
      section.status === 'error' && section.conversations.length === 0
  )
  const anyRows = visible.some((section) => section.rows.length > 0)

  let emptyLabel = 'No conversations yet'
  if (needle) emptyLabel = 'No conversations match that search'
  else if (filter === RAIL_FILTERS.needsAttention)
    emptyLabel = 'Nothing needs your attention'

  return (
    <div className="flex flex-col min-h-0 gap-5">
      <div className="flex flex-col gap-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search"
          aria-label="Search conversations"
          iconLeft={faMagnifyingGlass}
          inputClassName="adviser-search-input bg-neutral-0 placeholder:text-sm"
          className="w-full"
        />
        {onNewConversation && (
          <Button
            variant="secondary"
            size="sm"
            iconLeft={faPlus}
            onClick={onNewConversation}
            className="adviser-new-conversation-btn w-full"
          >
            New conversation
          </Button>
        )}
      </div>

      <div
        role="group"
        aria-label="Filter conversations"
        className="flex gap-2"
      >
        {Object.values(RAIL_FILTERS).map((option) => (
          <Button
            key={option}
            type="button"
            variant="ghost"
            aria-pressed={filter === option}
            onClick={() => setFilter(option)}
            className={mtc(
              'h-auto gap-2 rounded-full border border-neutral-200 bg-neutral-0 px-3 py-1 hover:bg-moss-25/50',
              filter === option && 'border-green-100 hover:bg-neutral-0'
            )}
          >
            <Text
              variant={TypographyStyles.BODY_XS}
              color={
                filter === option ? TextColors.HIGHLIGHT : TextColors.DEFAULT
              }
            >
              {FILTER_LABELS[option]}
            </Text>
            {option === RAIL_FILTERS.needsAttention &&
              needsAttentionCount > 0 && (
                <Badge
                  color="orange"
                  size="sm"
                  className="rounded-full bg-orange-100 px-1.5 py-0.5 text-xs leading-none"
                >
                  {needsAttentionCount > 99 ? '99+' : needsAttentionCount}
                </Badge>
              )}
          </Button>
        ))}
      </div>

      <div className="flex flex-col gap-4 -mx-3 pb-10 overflow-y-auto min-h-0 [mask-image:linear-gradient(to_bottom,black_calc(100%-40px),transparent)]">
        {visible.map((section) => {
          const open = Boolean(needle) || !collapsed.has(section.view)
          if (section.rows.length === 0 && section.status !== 'error')
            return null

          return (
            <section key={section.view} className="flex flex-col gap-0.5">
              <Button
                type="button"
                variant="ghost"
                onClick={() => toggleSection(section.view)}
                aria-expanded={open}
                className="h-auto w-full justify-between rounded-none bg-transparent px-3 pb-1 pt-0 hover:bg-transparent active:bg-transparent"
              >
                <Text
                  variant={TypographyStyles.EYEBROW}
                  color={TextColors.MUTED}
                >
                  {section.label}
                </Text>
                <Icon
                  icon={open ? faChevronUp : faChevronDown}
                  color="text-neutral-400"
                  className="text-[12px]"
                />
              </Button>
              {open && (
                <>
                  <ul className="flex flex-col gap-0.5">
                    {section.rows.map((row) => (
                      <li key={row.id}>
                        <ConversationRailRow
                          row={row}
                          active={row.id === activeConversationId}
                          emphasized={
                            section.view !== CONVERSATION_VIEWS.history
                          }
                          onSelect={onSelect}
                        />
                      </li>
                    ))}
                  </ul>
                  <SectionFooter section={section} onLoad={loadMore} />
                </>
              )}
            </section>
          )
        })}

        {loading && (
          <Text
            variant={TypographyStyles.BODY_XS}
            color={TextColors.MUTED}
            className="px-3 py-2"
          >
            Loading…
          </Text>
        )}

        {!loading && !anyRows && !failedEmpty && (
          <Text
            variant={TypographyStyles.BODY_XS}
            color={TextColors.MUTED}
            className="px-3 py-2"
          >
            {emptyLabel}
          </Text>
        )}
      </div>
    </div>
  )
}

const SectionFooter = ({
  section,
  onLoad,
}: {
  section: RailSection
  onLoad: (view: ConversationView) => void
}) => {
  if (section.status === 'error') {
    return (
      <div className="flex flex-col gap-1 px-3 py-2">
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          Could not load conversations.
        </Text>
        <Button
          type="button"
          variant="ghost"
          onClick={() => onLoad(section.view)}
          className="h-auto w-fit justify-start rounded-[12px] bg-transparent px-0 py-0 hover:bg-transparent"
        >
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.LINK}>
            Try again
          </Text>
        </Button>
      </div>
    )
  }

  if (!section.hasMore) return null

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={() => onLoad(section.view)}
      className="h-auto w-full justify-start rounded-[12px] bg-transparent px-3 py-2 text-left hover:bg-moss-50/30"
    >
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        Load more
      </Text>
    </Button>
  )
}
